import { StackScreenProps } from "@react-navigation/stack";
import { EditProductRootParamList } from "../../../../types";
import {
  useCommonFormActions,
  useProductFormActionsContextProvider,
} from "../../components/ProductForm/hooks";
import {
  Badge,
  Button,
  TextButton,
  Divider,
  Page,
  Stack,
  Text,
  Card,
  useFlashMessages,
  FlatActionList,
  Banner,
  ProductThumbnail,
  Tooltip,
} from "@towersystems/roam-common-native";
import React from "react";
import { useFormContext } from "react-hook-form";
import { ProductFormValues } from "../../components/ProductForm/types";
import { Colors, Spacings, View } from "react-native-ui-lib";
import { ProductForm } from "../../components/ProductForm";
import {
  MUTATION_UPDATE_PRODUCT,
  productFormDataToGraphqlInput,
  useGenerateProductBarcode,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import {
  Product,
  ProductKitItem,
} from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import { LocationIdToName } from "../../../../components";
import { useFocusEffect } from "@react-navigation/native";
import { useEditProductRootScreenContextProvider } from "../EditProductRootScreen/hooks";
import { ChildVariantsList } from "../../components/ChildVariantsList";
import { useTranslation } from "../../../../utilities/i18n";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import { KitItemsList } from "../../components/KitItemsList";
import { Ionicons } from "@expo/vector-icons";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export type EditProductScreenProps = StackScreenProps<
  EditProductRootParamList,
  "EditProductScreen"
>;

export const EditProductScreen = ({ navigation }: EditProductScreenProps) => {
  const { t } = useTranslation("product");
  const firstRender = React.useRef(true);
  const pageContext = useEditProductRootScreenContextProvider();
  const { onAfterAddProduct } = useProductFormActionsContextProvider() ?? {};
  const { existingProductId } = useCommonFormActions();
  const { handleSubmit, formState, setError, reset, watch } =
    useFormContext<ProductFormValues>();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const { product } = useSelectedProductEntityContextProvider();

  const [m] = useMutation<{ updateProduct: Product }>(MUTATION_UPDATE_PRODUCT);

  const { handleGenerateBarcode } = useGenerateProductBarcode();

  const flashMessagesContext = useFlashMessages();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const watchedLocationId = watch("locationId");
  const watchedSelectedOptions = watch("selectedOptions");
  const watchedGlobalDisable = watch("globalDisable");

  const disableForwardNavgiation = React.useMemo(() => {
    // We dont want to allow the user to enter a ender loop of navigating through children
    // products, and then back to parent.
    return (navigation.getParent()?.getState().index || 1) > 1;
  }, [navigation.getParent()?.getState().index || 0]);

  useFocusEffect(
    React.useCallback(() => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      pageContext.refreshProduct().catch(console.log);
    }, [])
  );

  React.useEffect(() => {
    const now = new Date();
    const localMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 30,
      0,
      0,
      0,
      0
    );

    const utcTimeForLocalMidnight = localMidnight.toISOString();

    navigation.setOptions({
      headerTitle(props) {
        if (strategyEnabled("product_summary_quick_win")) {
          return (
            <View>
              <Tooltip text="Generate AI insights using the last 30 days of sales data">
                <TextButton
                  onPress={() => {
                    navigation.navigate("ProductAiInsightsRootModalScreen", {
                      screen: "ProductAiInsightsScreen",
                      productId: product.id,
                      startDate: utcTimeForLocalMidnight,
                    });
                  }}
                  icon={<Ionicons name="sparkles" size={12} />}
                >
                  AI Insights
                </TextButton>
              </Tooltip>
            </View>
          );
        } else {
          return <View />;
        }
      },
      headerRight: () =>
        isDirty ? (
          <Button
            label="Reset"
            isDestructive
            onPress={() => {
              reset();
            }}
            size="compact"
          ></Button>
        ) : (
          <Button
            label={t("button.more_actions")}
            isAction
            onPress={() => {
              //@ts-ignore
              navigation.navigate("ProductActionsRootScreen");
            }}
            size="compact"
          ></Button>
        ),
    });
  }, [isDirty]);

  const disabled =
    !isDirty ||
    isLoading ||
    isSubmitting ||
    isValid == false ||
    isSubmitSuccessful ||
    !!existingProductId ||
    watchedGlobalDisable;

  const handleOnSubmit = React.useCallback(
    async (values: ProductFormValues) => {
      try {
        const result = await m({
          variables: {
            input: {
              ...productFormDataToGraphqlInput(values),
            },
          },
        });
        if (!result.data?.updateProduct) {
          throw new Error("Failed to create product");
        }
        await onAfterAddProduct?.(result.data.updateProduct, navigation);
      } catch (e: any) {
        const msg = e?.message ?? "Something went wrong";
        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });
        setError("root", {
          type: "serverError",
          message: msg,
        });
      }
      return null;
    },
    []
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.update_product")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const isRetailerProduct = product.stockCode && product.stockCode !== "0";

  const title = `${t("edit_product_title", {
    name: product.name ?? "",
  })} ${product.displayName ? ` - ${product.displayName}` : ""}`;

  const subtitle = isRetailerProduct
    ? `${t("edit_product_subtitle", {
        stock_number: product.stockNumber ?? "",
      })}`
    : undefined;

  const badges = [
    <Badge
      key={"RETAILER-PRODUCT"}
      productSource={
        isRetailerProduct ? "RETAILER-PRODUCT" : "NON-RETAILER-PRODUCT"
      }
    />,
  ];

  if (product?.variantParentId) {
    if (product.variantParentId == product.id) {
      badges.push(<Badge key="MAIN_PRODUCT" productType="MAIN_PRODUCT" />);
    } else {
      badges.push(
        <Badge key="VARIANT_PRODUCT" productType="VARIANT_PRODUCT" />
      );
    }
  }
  if (!!product?.productKit) {
    badges.push(<Badge key="KIT_PRODUCT" productType="KIT_PRODUCT" />);
  }
  if (!product.active) {
    badges.push(<Badge key="DISABLED" enabledStatus="DISABLED" />);
  }
  const locationTitleMarkup = watchedLocationId ? (
    <View style={{ marginBottom: Spacings.s5 }} row>
      <Text variant="heading4" fontWeight="bold">
        {t("card.pricing.title")}
      </Text>
      <Text>{` (`}</Text>
      <LocationIdToName
        locationId={watchedLocationId}
        variant="heading4"
        fontWeight="bold"
      />
      <Text>{`)`}</Text>
    </View>
  ) : undefined;

  const titleAccessory = (
    <ProductThumbnail size="large" imageUrl={product.image?.path} />
  );

  const currentInventoryPricing = React.useMemo(() => {
    const inventoryPricing = product.locationInventoryPricing
      ? product.locationInventoryPricing.find(
          (pricing) => pricing.locationId === watchedLocationId
        )
      : undefined;

    if (inventoryPricing) {
      return inventoryPricing;
    }

    return (
      product.locationInventoryPricing.find((p) => p.locationId === "0") ??
      undefined
    );
  }, [watchedLocationId]);

  const inventoryMarkup = currentInventoryPricing ? (
    <Card title={t("card.product_inventory.title")}>
      <FlatActionList
        items={[
          {
            key: "inventory",
            title:
              currentInventoryPricing.location?.name ??
              t("common:unknown_resource"),
            subTitle: `${currentInventoryPricing.stockOnHand} in stock`,
            componentRight() {
              return <View />;
            },
          },
          {
            key: "all",
            title: t("action.view_all_inventory.label"),
            isAction: true,
            subTitle: t("action.view_all_inventory.description"),
            onPress: () => {
              navigation.navigate("ProductViewInventoryLevelsScreen");
            },
          },
        ]}
      />
    </Card>
  ) : undefined;

  return (
    <Page
      title={title}
      secondaryTitle={subtitle}
      footer={footerMarkup}
      badges={<Stack spacing={Spacings.s1}>{badges}</Stack>}
      titleTrailingAccessory={titleAccessory}
    >
      <Stack vertical>
        {!!product.productKit && (
          <Banner status="warning" title="Editing is Disabled">
            <Text>Product Kits cannot be edited in Roam</Text>
          </Banner>
        )}

        {!!product.productKit && (
          <>
            <Card title="Kit Items">
              <KitItemsList
                kitItems={product.productKit.items || []}
                onAllPress={(kitItem: ProductKitItem) => {
                  //@ts-ignore
                  navigation.push("EditProductRootScreen", {
                    productId: kitItem.productId,
                  });
                }}
                disabled={disableForwardNavgiation}
              />
            </Card>
            <Divider />
          </>
        )}

        <Card title={t("card.product_details.title")}>
          <Stack vertical>
            <ProductForm.StockNumberFormInput
              onGenerateBarcodePress={handleGenerateBarcode}
            />

            <ProductForm.NameFormInput
              onProductNameLlmQuickWinButtonPress={() => {
                navigation.navigate(
                  "ProductSeoFriendlyNameLlmQuickWinRootScreen",
                  {
                    screen: "ProductSeoFriendlyNameReviewScreen",
                  }
                );
              }}
            />
            <ProductForm.DescriptionFormInput
              onLlmQuickWinButtonPress={() => {
                navigation.navigate("ProductDescriptionLlmQuickWinRootScreen", {
                  screen: "GenerateProductDescriptionRootScreen",
                  params: {
                    screen: "ProductDescriptionLlmQuickWinScreen",
                  },
                });
              }}
            />
            <ProductForm.PriceLookUpFormInput />
            <ProductForm.WeightFormInput
              onSelectUomPress={() => {
                navigation.navigate("ProductSelectUomScreen");
              }}
            />
          </Stack>
        </Card>

        {watchedSelectedOptions?.length > 0 ? (
          <>
            <Divider />
            <Card title={t("card.variant_options.title")}>
              {disableForwardNavgiation == false &&
                product.variantParentId &&
                product.id !== product.variantParentId && (
                  <>
                    <FlatActionList
                      items={[
                        {
                          key: "parent",
                          title:
                            product.variantParent?.name ??
                            t("action.edit_parent.title"),
                          subTitle: t("action.edit_parent.subtitle"),
                          onPress: () => {
                            //@ts-ignore
                            navigation.push("EditProductRootScreen", {
                              productId: product.variantParentId,
                            });
                          },
                          trailingAccessory: (
                            <ProductThumbnail
                              size="small"
                              imageUrl={product.variantParent?.image?.path}
                            />
                          ),
                        },
                      ]}
                    />
                    <Divider />
                  </>
                )}

              <ProductForm.SelectedOptionsFieldArray />
            </Card>
          </>
        ) : null}
        <Divider />
        {product?.variants?.length ? (
          <>
            {disableForwardNavgiation && (
              <Banner status="info" title={t("banner.subpage_access.title")}>
                <Text>{t("banner.subpage_access.description")}</Text>
              </Banner>
            )}
            <Card title="Other Variants">
              <ChildVariantsList
                variants={product.variants}
                onAllPress={(variant: Product) => {
                  //@ts-ignore
                  navigation.push("EditProductRootScreen", {
                    productId: variant.id,
                  });
                }}
                disabled={disableForwardNavgiation}
                onAddNewPress={() => {
                  navigation.push("ProductActionsRootScreen", {
                    screen: "ProductActionsCreateChildVariantScreen",
                  });
                }}
              />
            </Card>
            <Divider />
          </>
        ) : null}
        <Card title={t("card.categorization.title")}>
          <ProductForm.CategorizationFieldset
            onSelectCategoryPress={() =>
              navigation.navigate("ProductSelectCategoryScreen")
            }
            onSelectDepartmentPress={() => {
              navigation.navigate("ProductSelectDepartmentScreen");
            }}
            onManageTagsPress={() => {
              navigation.navigate("ManageProductTagsRootScreen", {
                screen: "ManageProductTagsScreen",
              });
            }}
            onTagsLlmQuickWinButtonPress={() => {
              navigation.navigate("ProductTagsLlmQuickWinRootScreen", {
                screen: "ProductTagsReviewScreen",
              });
            }}
          />
        </Card>
        <Divider />
        <Card
          title={t("card.pricing.title")}
          titleComponent={locationTitleMarkup}
        >
          <ProductForm.PricingFieldset
            onSelectTaxGroupPress={() => {
              navigation.navigate("ProductSelectTaxGroupScreen");
            }}
            onRetailPricingLlmQuickWinButtonPress={() => {
              navigation.navigate("ProductPricingLlmQuickWinRootScreen", {
                screen: "ProductPricingReviewScreen",
              });
            }}
          />
          {currentInventoryPricing?.cataloguePrice ? (
            <>
              <Divider />
              <FlatActionList
                items={[
                  {
                    key: "cataloguePrice",
                    title: `Catalogue Price Found`,
                    subTitle:
                      "Catalogue price will be used instead of retail price for this location!",
                    componentRight() {
                      return (
                        <Text variant="heading1">
                          {moneyToFormattedString(
                            currentInventoryPricing.cataloguePrice
                          )}
                        </Text>
                      );
                    },
                  },
                ]}
              />
            </>
          ) : null}
        </Card>
        <Divider />
        {inventoryMarkup}
        {inventoryMarkup && <Divider />}
        <Card title={t("card.supplier.title")}>
          <ProductForm.SupplierFieldset
            onSelectSupplierPress={() => {
              navigation.navigate("ProductSelectSupplierScreen");
            }}
          />
        </Card>
        <Divider />
        <Card title={t("card.serial_numbers.title")}>
          <ProductForm.SerialNumbersFieldset
            onManageSerialNumbersPress={() => {
              navigation.navigate("ProductViewSerialNumbersScreen");
            }}
          />
        </Card>
      </Stack>
    </Page>
  );
};
